import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { SUPPORTED_CITIES, useCity, type City } from "@/contexts/CityContext";
import { cn } from "@/lib/utils";
import { cityForName, CITIES } from "@/seo/shared";

/**
 * La misma página en otra ciudad (29-10-2026, usuario: al cambiar de ciudad
 * en una ficha seguían los pases de la anterior). En las rutas con ciudad,
 * la URL manda, así que cambiar solo la ciudad global no bastaba:
 * /{ciudad}, /{ciudad}/{landing} y /{ciudad}/pelicula/{slug} pasan a la otra
 * ciudad. Si la película no se proyecta allí, su ficha lo dice y enlaza a la
 * ciudad donde sí está. El resto de rutas no llevan ciudad y no se tocan.
 */
function pathInCity(pathname: string, city: City): string | null {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length || !CITIES.some((c) => c.slug === parts[0])) return null;
  const target = cityForName(city);
  if (!target || target.slug === parts[0]) return null;
  return "/" + [target.slug, ...parts.slice(1)].join("/");
}

/** Forma corta para el trigger en móvil (el nombre completo no cabe). */
const CITY_SHORT: Record<City, string> = { Barcelona: "BCN", Valencia: "VLC" };

/** Selector de ciudad de la cabecera. Cambia la ciudad global: filtra la
 *  landing y resiembra el filtro de la cartelera; el visitante puede seguir
 *  explorando otras ciudades desde los filtros.
 *
 *  Exploración a todo color (30-10-2026): solo la ciudad activa, en corto y
 *  subrayada («VLC»), sin pastilla (más discreto que «Iniciar sesión»); al
 *  pulsarla se despliega la lista. */
export function CitySelector({ solid }: { solid: boolean }) {
  const { t } = useTranslation();
  const { city, setCity } = useCity();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Fuera de escritorio el desplegable se coloca respecto a la cabecera (fija),
  // no respecto al botón: «VLC» va en medio y, alineado a él, se salía por la
  // izquierda de la pantalla (31-10-2026).
  return (
    <div ref={ref} className="lg:relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={city ? `${t("city.switcherAria")}: ${city}` : t("city.switcherAria")}
        className={cn(
          // En voz de datos (1-11-2026): «Ciudad», sin ciudad elegida, salía en
          // minúsculas junto a una barra toda en mayúsculas.
          "data-voice inline-flex items-center gap-1 rounded px-0.5 py-1 text-sm underline decoration-2 underline-offset-4 transition-opacity duration-150 hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 active:scale-95",
          solid ? "text-rc-navy focus-visible:ring-rc-violet" : "text-white focus-visible:ring-rc-lime"
        )}
      >
        {/* Sin ciudad, «Ciudad» y no «Cambiar de ciudad»: en el móvil el texto
            largo encogía el logo (31-10-2026). */}
        {city ? CITY_SHORT[city] : t("movies.sections.city")}
        <svg className={cn("h-3.5 w-3.5 transition-transform duration-150", open && "rotate-180")} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        // Exploración: mismo desplegable que las opciones de la frase de la
        // cartelera (blanco, recto, título pequeño y botones).
        <div className="absolute right-4 top-full z-50 mt-2 flex w-max max-w-[min(20rem,calc(100vw-2rem))] flex-col gap-3 border-2 border-rc-navy bg-white p-4 sm:right-6 lg:right-0">
          <span className="data-voice text-[11px] font-bold text-rc-navy/70">{t("movies.sections.city")}</span>
          <ul
            role="listbox"
            aria-label={t("city.switcherAria")}
            className="flex flex-wrap gap-2"
          >
            {SUPPORTED_CITIES.map((c) => {
              const active = c === city;
              return (
                <li key={c}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      setCity(c, "switcher");
                      setOpen(false);
                      const next = pathInCity(pathname, c);
                      if (next) navigate(next);
                    }}
                    className={cn(
                      "data-voice rounded-full border-2 border-rc-navy px-3.5 py-1.5 text-xs font-bold transition-[transform,background-color,color] duration-150 ease-spring active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet",
                      active ? "bg-rc-navy text-white" : "bg-white text-rc-navy hover:bg-rc-lime"
                    )}
                  >
                    {c}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
